import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Users, UserCog, Link2, User, LogOut, ChevronDown, ClipboardList } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  super_admin: "Super Admin",
  marketing: "Marketing",
  cco: "CCO",
};

const allNavItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/leads", icon: Users, label: "Leads" },
  { path: "/team", icon: UserCog, label: "Team" },
  { path: "/integrations", icon: Link2, label: "Integrations" },
  { path: "/audit-log", icon: ClipboardList, label: "Audit" },
];

const TopNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = allNavItems.filter(item => {
    if (user?.role === "cco" && item.path === "/integrations") return false;
    return true;
  });

  const getInitials = (name) => name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const profilePicUrl = user?.profile_pic ? `${API.replace('/api', '')}${user.profile_pic}` : null;

  return (
    <>
      {/* Desktop/Tablet Top Nav */}
      <header
        className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 shadow-sm"
        data-testid="top-nav"
      >
        <div className="max-w-[1600px] mx-auto h-full px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_campaign-leads-1/artifacts/9fqxo1et_PomenLogo_blueBG.png"
                alt="POMEN Logo"
                className="h-9 w-auto rounded-lg"
              />
              <div className="hidden sm:block">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">Lead Tracker</p>
              </div>
            </div>

            {/* Desktop nav links */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-[#0D0D5B] text-white"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8 border border-gray-200">
                    <AvatarImage src={profilePicUrl} alt={user?.name} />
                    <AvatarFallback className="bg-[#0D0D5B] text-white text-xs font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900 leading-tight">{user?.name}</p>
                    <p className="text-[10px] text-gray-500 leading-tight">{roleLabels[user?.role] || user?.role}</p>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer" data-testid="nav-profile">
                  <User className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Nav Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-200 z-[60] pb-[env(safe-area-inset-bottom)]"
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around h-16 px-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                  isActive ? "text-[#0D0D5B]" : "text-gray-400"
                }`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <div className={`flex items-center justify-center w-10 h-7 rounded-full transition-colors ${isActive ? "bg-[#0D0D5B]/10" : ""}`}>
                    <item.icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] leading-tight ${isActive ? "font-semibold" : "font-medium"}`}>
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
};

export default TopNav;
